import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Space,
  message,
  Card,
  Tag,
  Popconfirm,
  Switch,
  Tooltip,
  Row,
  Col,
  Statistic,
} from "antd";
import {
  Plus,
  RefreshCw,
  Edit,
  Trash2,
  AlertTriangle,
  Bell,
  BellOff,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import apiService from "../../services/api";
import {
  setRules,
  addRule,
  updateRule,
  removeRule,
  setLoading,
  setError,
  selectAlertRules,
  selectAlertLoading,
} from "../../store/alertSlice";
import AlertRuleForm from "./AlertRuleForm";
import { formatDateTime } from "../../utils/formatters";

const AlertRuleList = () => {
  const dispatch = useDispatch();
  const rules = useSelector(selectAlertRules);
  const loading = useSelector(selectAlertLoading);
  const [showModal, setShowModal] = useState(false);
  const [currentRule, setCurrentRule] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const loadRules = useCallback(async () => {
    dispatch(setLoading(true));
    try {
      const params = {
        page: pagination.current,
        page_size: pagination.pageSize,
      };

      const response = await apiService.alertRules.getRules(params);
      if (!response.data.success) {
        throw new Error(response.data.message || "获取告警规则失败");
      }

      dispatch(setRules(response.data.data?.list || []));
      setPagination((prev) => ({
        ...prev,
        total: response.data.data?.total || 0,
      }));
    } catch (err) {
      message.error(err.message || "获取告警规则失败");
      dispatch(setError(err.message));
    }
  }, [dispatch, pagination.current, pagination.pageSize]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const handleCreate = () => {
    setCurrentRule(null);
    setShowModal(true);
  };

  const handleEdit = (record) => {
    setCurrentRule(record);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await apiService.alertRules.deleteRule(id);
      if (!response.data.success) {
        throw new Error(response.data.message || "删除失败");
      }
      message.success("删除成功");
      dispatch(removeRule(id));
    } catch (err) {
      message.error(err.message || "删除失败");
    }
  };

  const handleToggle = async (id, enabled) => {
    try {
      const response = await apiService.alertRules.toggleRule(id);
      if (!response.data.success) {
        throw new Error(response.data.message || "切换状态失败");
      }
      message.success(enabled ? "已禁用" : "已启用");
      dispatch(updateRule(response.data.data));
    } catch (err) {
      message.error(err.message || "切换状态失败");
    }
  };

  const handleFormSubmit = async (values) => {
    try {
      if (currentRule) {
        const response = await apiService.alertRules.updateRule(currentRule.id, values);
        if (!response.data.success) {
          throw new Error(response.data.message || "更新失败");
        }
        message.success("更新成功");
        dispatch(updateRule(response.data.data));
      } else {
        const response = await apiService.alertRules.createRule(values);
        if (!response.data.success) {
          throw new Error(response.data.message || "创建失败");
        }
        message.success("创建成功");
        dispatch(addRule(response.data.data));
      }
      setShowModal(false);
      loadRules();
    } catch (err) {
      message.error(err.message || "操作失败");
    }
  };

  const getSeverityTag = (severity) => {
    const colors = {
      critical: "red",
      warning: "orange",
      info: "blue",
    };
    const labels = {
      critical: "严重",
      warning: "警告",
      info: "信息",
    };
    return <Tag color={colors[severity]}>{labels[severity]}</Tag>;
  };

  const getTypeLabel = (type) => {
    const labels = {
      trade_volume: "交易量",
      balance: "余额",
      pnl: "盈亏",
      data_missing: "数据缺失",
    };
    return labels[type] || type;
  };

  const columns = [
    {
      title: "规则名称",
      dataIndex: "name",
      key: "name",
      width: 200,
    },
    {
      title: "类型",
      dataIndex: "type",
      key: "type",
      width: 100,
      render: (type) => getTypeLabel(type),
    },
    {
      title: "严重程度",
      dataIndex: "severity",
      key: "severity",
      width: 100,
      render: (severity) => getSeverityTag(severity),
    },
    {
      title: "指标",
      dataIndex: "metric",
      key: "metric",
      width: 150,
    },
    {
      title: "条件",
      key: "condition",
      width: 150,
      render: (_, record) => {
        const operators = {
          gt: ">",
          lt: "<",
          eq: "=",
          change_rate: "变化率",
        };
        return `${operators[record.operator]} ${record.threshold}`;
      },
    },
    {
      title: "状态",
      dataIndex: "enabled",
      key: "enabled",
      width: 80,
      render: (enabled, record) => (
        <Switch
          checked={enabled}
          onChange={() => handleToggle(record.id, enabled)}
          checkedChildren="启用"
          unCheckedChildren="禁用"
        />
      ),
    },
    {
      title: "创建时间",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      render: (text) => formatDateTime(text),
    },
    {
      title: "操作",
      key: "action",
      width: 150,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="编辑">
            <Button
              type="link"
              size="small"
              icon={<Edit size={16} />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定要删除这条规则吗?"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button
                type="link"
                size="small"
                danger
                icon={<Trash2 size={16} />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Statistic
              title="总规则数"
              value={pagination.total}
              prefix={<AlertTriangle size={20} />}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="已启用"
              value={rules.filter((r) => r.enabled).length}
              prefix={<Bell size={20} />}
              valueStyle={{ color: "#3f8600" }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="已禁用"
              value={rules.filter((r) => !r.enabled).length}
              prefix={<BellOff size={20} />}
              valueStyle={{ color: "#cf1322" }}
            />
          </Col>
        </Row>

        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button
              type="primary"
              icon={<Plus size={16} />}
              onClick={handleCreate}
            >
              新建规则
            </Button>
            <Button icon={<RefreshCw size={16} />} onClick={loadRules}>
              刷新
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={rules}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPagination((prev) => ({ ...prev, current: page, pageSize }));
            },
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal
        title={currentRule ? "编辑告警规则" : "新建告警规则"}
        open={showModal}
        onCancel={() => setShowModal(false)}
        footer={null}
        width={600}
        destroyOnClose
      >
        <AlertRuleForm
          initialValues={currentRule}
          onSubmit={handleFormSubmit}
          onCancel={() => setShowModal(false)}
        />
      </Modal>
    </div>
  );
};

export default AlertRuleList;
